import { useMemo } from 'react';
import { PRIORITY_LABEL, PRIORITY_ORDER } from '@planning/contracts';
import { Tabla, type ColumnaTabla } from '@xiltrion/ui/bloques/tabla';
import { AvatarPersona } from '@xiltrion/ui/bloques/personas';
import { EtiquetaColor } from '@xiltrion/ui/bloques/etiquetas-color';
import { EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { Checkbox } from '@xiltrion/ui/checkbox';
import { Icono } from '@xiltrion/ui/icono';
import { cn } from '@xiltrion/ui/utils';
import { useLabels, usePersonas, useProjects, useTeams, useUpdateTask } from '@/api/hooks';
import type { Label, TaskItem } from '@/api/types';
import type { ViewProps } from '@/app/extension-points';
import { useTerms } from '@/app/session';
import { QuickAdd } from '../tasks/QuickAdd';
import { applyFilters, formatDate, isOverdue } from '../tasks/view-logic';

/** Vista Tabla (spec §3.1): las mismas tareas como hoja, agrupadas por sección (u otro campo) y con columnas configurables. */
export function TableView({ project, tasks, view, onOpenTask, loading }: ViewProps) {
  const { t } = useTerms();
  const { porId: people } = usePersonas();
  const { byId: teams } = useTeams();
  const { data: globalLabels = [] } = useLabels();
  const { data: projects = [] } = useProjects();
  const labels = useMemo(() => new Map<string, Label>([...globalLabels, ...(project?.labels ?? [])].map((l) => [l.id, l])), [globalLabels, project?.labels]);
  const sections = new Map((project?.sections ?? []).map((s) => [s.id, s.name]));
  const projectName = new Map(projects.map((p) => [p.id, p.name]));
  const update = useUpdateTask();
  const canEdit = project ? project.capabilities.editTasks : true;
  const rows = applyFilters(tasks, view?.config);
  const groupBy = view?.config.groupBy ?? (project ? 'section' : null);

  const columnas: ColumnaTabla<TaskItem>[] = [
    {
      key: 'done', titulo: '', fija: true, ordenable: false, ancho: '36px', valor: (r) => (r.completedAt ? 1 : 0),
      render: (r) => (
        <Checkbox forma="circular" checked={!!r.completedAt} disabled={!canEdit} onCheckedChange={() => update.mutate({ id: r.id, patch: { completed: !r.completedAt } })} onClick={(e) => e.stopPropagation()} aria-label={r.completedAt ? 'Reabrir' : 'Completar'} />
      ),
    },
    { key: 'number', titulo: '#', valor: (r) => r.number, numerico: true, ancho: '64px', render: (r) => <span className="text-muted-foreground">T-{r.number}</span> },
    { key: 'title', titulo: t('task'), valor: (r) => r.title, lineas: 2, render: (r) => <span className={cn('font-medium', r.completedAt && 'text-muted-foreground line-through')}>{r.blocked && <Icono nombre="mdi:lock-outline" className="mr-1 inline size-3.5 text-destructive" />}{r.title}</span>, secundario: (r) => (r.subtaskCount ? `${r.subtaskDone}/${r.subtaskCount} subtareas` : r.checklistTotal ? `${r.checklistDone}/${r.checklistTotal} en la lista` : undefined) },
    ...(project ? [{ key: 'section', titulo: t('section'), valor: (r: TaskItem) => sections.get(r.sectionId ?? '') ?? '', unaLinea: true }] : [{ key: 'project', titulo: t('project'), valor: (r: TaskItem) => projectName.get(r.projectId) ?? '', unaLinea: true }]),
    { key: 'assignee', titulo: 'Responsable', valor: (r) => people.get(r.assigneeId ?? '')?.nombre ?? '', render: (r) => (r.assigneeId ? <span className="inline-flex items-center gap-1.5"><AvatarPersona persona={people.get(r.assigneeId)} tamano="xs" />{people.get(r.assigneeId)?.nombre}</span> : r.teamId ? <span className="text-warning">Sin repartir</span> : <span className="text-muted-foreground">—</span>), unaLinea: true },
    { key: 'team', titulo: t('team'), valor: (r) => teams.get(r.teamId ?? '')?.name ?? '', render: (r) => teams.get(r.teamId ?? '')?.name ?? <span className="text-muted-foreground">—</span>, unaLinea: true },
    { key: 'dueDate', titulo: 'Vence', valor: (r) => r.dueDate ?? '', render: (r) => <span className={cn('tnum', isOverdue(r) && 'font-medium text-destructive')}>{formatDate(r.dueDate) || '—'}</span>, unaLinea: true },
    { key: 'priority', titulo: 'Prioridad', valor: (r) => (r.priority ? PRIORITY_ORDER.indexOf(r.priority) : 9), render: (r) => (r.priority ? PRIORITY_LABEL[r.priority] : <span className="text-muted-foreground">—</span>), unaLinea: true },
    { key: 'labels', titulo: 'Etiquetas', valor: (r) => r.labelIds.map((id) => labels.get(id)?.name).join(', '), ordenable: false, render: (r) => <span className="flex flex-wrap gap-1">{r.labelIds.map((id) => labels.get(id)).filter(Boolean).map((l) => <EtiquetaColor key={l!.id} etiqueta={{ nombre: l!.name, color: l!.color }} tamano="xs" />)}</span> },
    { key: 'time', titulo: 'Tiempo', valor: (r) => r.minutesLogged, numerico: true, render: (r) => (r.minutesLogged ? `${(r.minutesLogged / 60).toFixed(1)} h` : '') },
    ...(project?.customFields ?? []).map((f) => ({
      key: `cf:${f.id}`, titulo: f.name, unaLinea: true,
      valor: (r: TaskItem) => {
        const v = r.customFields[f.id];
        if (f.type === 'SELECT') return f.options.find((o) => o.value === v)?.label ?? '';
        if (f.type === 'PERSON') return people.get(String(v ?? ''))?.nombre ?? '';
        return v === null || v === undefined ? '' : String(v);
      },
    })),
  ];

  if (!loading && !tasks.length) {
    return (
      <div className="p-6">
        <EstadoVacio titulo={`Aún no hay ${t('task', true).toLowerCase()}`} descripcion="Crea la primera y aparecerá en todas las vistas." accion={project && canEdit ? <QuickAdd projectId={project.id} label="Nueva tarea" className="w-72" /> : undefined} borde />
      </div>
    );
  }
  return (
    <div className="space-y-2 p-4">
      <Tabla
        columnas={columnas}
        filas={rows}
        claveFila={(r) => r.id}
        onClicFila={(r) => onOpenTask(r.id)}
        cargando={loading}
        densa
        columnasVisibles={view?.config.visibleFields}
        agrupar={groupBy && (groupBy === 'section' || groupBy === 'project' || groupBy === 'team') ? { por: groupBy } : undefined}
        virtual={rows.length > 300 ? { alto: '70vh' } : undefined}
        claseFila={(r) => (r.completedAt ? 'opacity-70' : undefined)}
        barra={project && canEdit ? <QuickAdd projectId={project.id} label="Nueva tarea" className="w-64" /> : undefined}
      />
    </div>
  );
}
